<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useBalanceStore } from '../stores/balance';
import { useUsageStore } from '../stores/usage';
import { formatNgonka, ngonkaToGnk } from '../lib/format';
import UsageChart from '../components/UsageChart.vue';
import type { UsageDailyBucket } from '../api/usage';

const balance = useBalanceStore();
const usage = useUsageStore();
const loadingKpiLabels = ['Total spent', 'Total tokens', 'Requests', 'Rate'] as const;

onMounted(() => {
  void loadAll();
});

async function loadAll(): Promise<void> {
  await Promise.all([balance.load(), usage.loadSummary()]);
}

const primaryRate = computed(() => usage.summary?.rate_per_1m?.[0] ?? null);

const welcomeTotal = computed<bigint>(() => parseBigInt(usage.summary?.welcome_bonus_total));
const welcomeUsed = computed<bigint>(() => parseBigInt(usage.summary?.welcome_bonus_used));
const hasWelcomeBonus = computed<boolean>(() => welcomeTotal.value > 0n);

const welcomeRemaining = computed<bigint>(() => {
  const remaining = welcomeTotal.value - welcomeUsed.value;
  return remaining > 0n ? remaining : 0n;
});

const welcomeProgressPct = computed<number>(() => {
  const total = Number(welcomeTotal.value);
  const used = Number(welcomeUsed.value);
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(used)) return 0;
  return Math.min(100, Math.max(0, (used / total) * 100));
});

const tokensRemainingEstimate = computed<number | null>(() => {
  const usd = balance.data?.balance_usd;
  const rate = primaryRate.value?.value_usd;
  if (usd == null || !Number.isFinite(usd) || rate == null || !Number.isFinite(rate) || rate <= 0) {
    return null;
  }
  return Math.max(0, Math.floor((usd / rate) * 1_000_000));
});

const usageSeries = computed<UsageDailyBucket[]>(() => buildUsageSeries(usage.summary?.last_30d_daily ?? []));

const hasChartData = computed<boolean>(() =>
  usageSeries.value.some((bucket) => bucket.requests > 0 || bucket.tokens > 0 || bucket.cost_usd > 0),
);

const totalWindowRequests = computed<number>(() =>
  usageSeries.value.reduce((sum, bucket) => sum + bucket.requests, 0),
);

function parseBigInt(value: string | null | undefined): bigint {
  if (!value) return 0n;
  try {
    return BigInt(value);
  } catch {
    return 0n;
  }
}

function formatDigits(raw: string): string {
  const negative = raw.startsWith('-');
  const digits = negative ? raw.slice(1) : raw;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}`;
}

function formatInteger(value: number | bigint | null | undefined): string {
  if (value == null) return '—';
  if (typeof value === 'bigint') return formatDigits(value.toString());
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(value));
}

function formatCompactTokens(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(2)}K`;
  return formatInteger(value);
}

function formatUsdMetric(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  if (value === 0) return '$0.0';

  const abs = Math.abs(value);
  if (abs >= 1) {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const fixed = value.toFixed(8);
  const [whole, fraction = ''] = fixed.split('.');
  const groupedWhole = Number(whole).toLocaleString('en-US');
  const trimmedFraction = fraction.replace(/0+$/, '');
  return trimmedFraction.length > 0 ? `$${groupedWhole}.${trimmedFraction}` : `$${groupedWhole}`;
}

function parseUtcDay(date: string): Date {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return normalizeUtcDay(new Date());
  return normalizeUtcDay(parsed);
}

function normalizeUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function shiftUtcDays(date: Date, delta: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + delta);
  return next;
}

function formatDayKey(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildUsageSeries(raw: UsageDailyBucket[]): UsageDailyBucket[] {
  const buckets = new Map<string, UsageDailyBucket>();
  let end = normalizeUtcDay(new Date());

  for (const bucket of raw) {
    buckets.set(bucket.date, bucket);
    const parsed = parseUtcDay(bucket.date);
    if (parsed.getTime() > end.getTime()) end = parsed;
  }

  const start = shiftUtcDays(end, -29);
  const series: UsageDailyBucket[] = [];

  for (let offset = 0; offset < 30; offset += 1) {
    const date = shiftUtcDays(start, offset);
    const key = formatDayKey(date);
    const bucket = buckets.get(key);
    series.push({
      date: key,
      tokens: bucket?.tokens ?? 0,
      requests: bucket?.requests ?? 0,
      cost_usd: bucket?.cost_usd ?? 0,
    });
  }

  return series;
}
</script>

<template>
  <div class="max-w-6xl mx-auto w-full px-6 lg:px-10 py-8 flex flex-col gap-6">
    <header class="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h1 class="font-display text-4xl font-semibold tracking-tight">Overview</h1>
        <p class="text-muted text-sm mt-2">
          Monitor balance, usage, pricing, and API readiness.
        </p>
      </div>

      <router-link to="/billing/topup" class="btn-primary">
        Top up
      </router-link>
    </header>

    <section class="grid gap-4 xl:grid-cols-2">
      <article class="card p-6 flex flex-col gap-4 min-h-[220px]">
        <div class="text-xs uppercase tracking-[0.2em] text-muted">Account balance</div>

        <div v-if="balance.loading && !balance.data" class="text-sm text-muted">
          Loading balance…
        </div>
        <template v-else-if="balance.error && !balance.data">
          <div class="text-sm text-danger">{{ balance.error }}</div>
          <button type="button" class="btn-ghost text-xs self-start" @click="loadAll">
            Retry
          </button>
        </template>
        <template v-else-if="balance.data">
          <div class="font-display text-4xl font-semibold">
            {{ formatUsdMetric(balance.data.balance_usd) }}
          </div>

          <p class="text-sm text-muted">
            <span v-if="tokensRemainingEstimate !== null">
              ≈ {{ formatInteger(tokensRemainingEstimate) }} tokens remaining
            </span>
            <span v-else>
              Approximate token runway appears once pricing data is available.
            </span>
          </p>

          <div class="text-xs text-muted flex flex-wrap gap-x-3 gap-y-1">
            <span>{{ ngonkaToGnk(balance.data.balance_ngonka) }} GNK</span>
            <span>{{ formatNgonka(balance.data.balance_ngonka) }} ngonka</span>
          </div>

          <router-link to="/billing/topup" class="btn-primary mt-auto self-start">
            Top up with crypto
          </router-link>
        </template>
      </article>

      <article class="card p-6 flex flex-col gap-4 min-h-[220px]">
        <div class="text-xs uppercase tracking-[0.2em] text-muted">Welcome bonus</div>

        <div v-if="usage.summaryLoading && !usage.summary" class="text-sm text-muted">
          Loading usage summary…
        </div>
        <template v-else-if="usage.summaryError && !usage.summary">
          <div class="text-sm text-danger">{{ usage.summaryError }}</div>
          <button type="button" class="btn-ghost text-xs self-start" @click="usage.loadSummary()">
            Retry
          </button>
        </template>
        <template v-else>
          <div class="flex items-end gap-3 flex-wrap">
            <div class="font-display text-4xl font-semibold text-ok">
              {{ formatInteger(welcomeRemaining) }}
            </div>
            <div class="text-lg text-ok mb-1">tokens</div>
          </div>

          <p class="text-sm text-muted">
            <span v-if="hasWelcomeBonus">
              {{ formatInteger(welcomeUsed) }} of {{ formatInteger(welcomeTotal) }} used
            </span>
            <span v-else>No welcome bonus active.</span>
          </p>

          <div class="mt-auto">
            <div class="h-2 bg-code rounded-full overflow-hidden">
              <div
                class="h-full rounded-full bg-ink transition-all"
                :style="{ width: `${welcomeProgressPct}%` }"
              />
            </div>
          </div>
        </template>
      </article>
    </section>

    <section class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      <template v-if="usage.summaryLoading && !usage.summary">
        <article
          v-for="label in loadingKpiLabels"
          :key="label"
          class="card p-5 bg-surface-soft min-h-[144px]"
        >
          <div class="text-xs uppercase tracking-[0.2em] text-muted">{{ label }}</div>
          <div class="text-sm text-muted mt-6">Loading…</div>
        </article>
      </template>

      <article
        v-else-if="usage.summaryError && !usage.summary"
        class="card p-5 border-danger text-danger md:col-span-2 xl:col-span-4"
      >
        <div class="flex items-center justify-between gap-3 flex-wrap">
          <span>{{ usage.summaryError }}</span>
          <button type="button" class="btn-ghost text-xs" @click="usage.loadSummary()">
            Retry usage summary
          </button>
        </div>
      </article>

      <template v-else-if="usage.summary">
        <article class="card p-5 bg-surface-soft min-h-[144px]">
          <div class="text-xs uppercase tracking-[0.2em] text-muted">Total spent</div>
          <div class="text-3xl font-semibold mt-4">{{ formatUsdMetric(usage.summary.total_spent_usd) }}</div>
          <p class="text-sm text-muted mt-2">all time</p>
        </article>

        <article class="card p-5 bg-surface-soft min-h-[144px]">
          <div class="text-xs uppercase tracking-[0.2em] text-muted">Total tokens</div>
          <div class="text-3xl font-semibold mt-4">{{ formatInteger(usage.summary.total_tokens) }}</div>
          <p class="text-sm text-muted mt-2">
            {{ formatCompactTokens(usage.summary.total_tokens_in) }} in /
            {{ formatCompactTokens(usage.summary.total_tokens_out) }} out
          </p>
        </article>

        <article class="card p-5 bg-surface-soft min-h-[144px]">
          <div class="text-xs uppercase tracking-[0.2em] text-muted">Requests</div>
          <div class="text-3xl font-semibold mt-4">{{ formatInteger(usage.summary.total_requests) }}</div>
          <p class="text-sm text-muted mt-2">all time</p>
        </article>

        <article class="card p-5 bg-surface-soft min-h-[144px]">
          <div class="text-xs uppercase tracking-[0.2em] text-muted">Rate</div>
          <div class="text-3xl font-semibold mt-4">
            {{ formatUsdMetric(primaryRate?.value_usd ?? null) }}
          </div>
          <p class="text-sm text-muted mt-2">
            per 1M tokens ({{ primaryRate?.model ?? 'unavailable' }})
          </p>
        </article>
      </template>
    </section>

    <section class="card p-6">
      <div class="flex items-baseline justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 class="font-display text-2xl font-semibold">Usage (Last 30 Days)</h2>
          <p class="text-xs text-muted mt-1">Daily requests from the usage summary API.</p>
        </div>

        <div v-if="usage.summary && hasChartData" class="text-xs text-muted">
          {{ formatInteger(totalWindowRequests) }} requests in this window
        </div>
      </div>

      <div
        v-if="usage.summaryLoading && !usage.summary"
        class="min-h-[320px] flex items-center justify-center text-sm text-muted"
      >
        Loading chart…
      </div>

      <div
        v-else-if="usage.summaryError && !usage.summary"
        class="min-h-[320px] flex flex-col items-center justify-center gap-3 text-sm text-danger text-center p-6"
      >
        <span>{{ usage.summaryError }}</span>
        <button type="button" class="btn-ghost text-xs" @click="usage.loadSummary()">
          Retry usage summary
        </button>
      </div>

      <UsageChart v-else-if="hasChartData" :points="usageSeries" />

      <div
        v-else
        class="min-h-[320px] border border-dashed border-border rounded-lg flex flex-col items-center justify-center text-center text-sm text-muted p-6"
      >
        <div class="text-base text-ink">No usage yet in the last 30 days.</div>
        <p class="mt-2 max-w-md">
          Your first successful API request will appear here once the router records it.
        </p>
      </div>
    </section>
  </div>
</template>